import React from 'react';
import { ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function handleRowToggleKeyDown(e: React.KeyboardEvent<HTMLElement>, onToggle: () => void): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onToggle();
  }
}

export function CommentToggleButton({
  isExpanded,
  onToggle,
  ariaLabel,
  iconClassName = 'w-4 h-4',
}: {
  isExpanded: boolean;
  onToggle: () => void;
  ariaLabel: string;
  iconClassName?: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      className={`h-10 w-10 p-0 inline-flex items-center justify-center rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-gray-300 ${isExpanded ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      aria-expanded={isExpanded}
      aria-label={ariaLabel}
    >
      {isExpanded
        ? <ChevronUp className={`${iconClassName} text-gray-600`} size={16} />
        : <ChevronDown className={`${iconClassName} text-gray-500`} size={16} />}
    </button>
  );
}

export function ExpandedCommentRow({
  isExpanded,
  colSpan,
  comment,
  useTranslation: shouldTranslate,
}: {
  isExpanded: boolean;
  colSpan: number;
  comment?: string;
  useTranslation?: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation('scenario');
  
  if (!isExpanded) return null;
  
  const displayComment = shouldTranslate && comment ? t(comment) : (comment || '-');
  
  return (
    <tr className="bg-blue-50 animate-in fade-in slide-in-from-top-1 duration-200 border-b border-gray-100">
      <td className="px-4 pb-2 pt-0 text-center" aria-hidden />
      <td colSpan={colSpan} className="px-4 pb-2 pt-0">
        <div className="p-4 rounded-lg shadow-sm mt-2">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-full text-blue-500 border border-blue-500">
              <MessageSquare size={20} />
            </div>
            <div className="flex-1">
              <div 
                className="text-gray-900 text-sm leading-relaxed whitespace-pre-wrap text-start"
                dangerouslySetInnerHTML={{ __html: displayComment.trim() }}
              />
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export function ExpandableCommentTableRows({
  isExpanded,
  toggleComment,
  colSpan,
  comment,
  useTranslation: shouldTranslate,
  children,
}: {
  isExpanded: boolean;
  toggleComment: () => void;
  colSpan: number;
  comment?: string;
  useTranslation?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <>
      <tr
        className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 cursor-pointer"
        onClick={toggleComment}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => handleRowToggleKeyDown(e, toggleComment)}
      >
        {children}
      </tr>
      <ExpandedCommentRow isExpanded={isExpanded} colSpan={colSpan} comment={comment} useTranslation={shouldTranslate} />
    </>
  );
}
